import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { PipelineView } from "./views/PipelineView";
import { ScheduledCallsView } from "./views/ScheduledCallsView";
import { CalendarView } from "./views/CalendarView";
import { SettingsView } from "./views/SettingsView";
import { useEventsIndicators } from "./useEventsIndicators";

const SUB_TABS = [
  { to: "", label: "Pipeline", end: true },
  { to: "calls", label: "Scheduled Calls", end: false },
  { to: "calendar", label: "Calendar", end: false },
  { to: "settings", label: "Settings", end: false },
] as const;

function TabCount({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white"
      aria-label={`${count} scheduled`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function EventsModule() {
  const { scheduledCalls } = useEventsIndicators(true);
  const countFor = (to: string) => (to === "calls" ? scheduledCalls : 0);

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-tribal-border bg-tribal-surface px-4 py-3">
        <div className="flex min-w-0 items-center gap-4">
          <h1 className="text-lg font-semibold tracking-tight">
            <span className="text-tribal-red">Tribal Axe</span> Events
          </h1>
          <nav
            aria-label="Events views"
            className="hidden items-center gap-0.5 rounded-lg bg-black/30 p-0.5 sm:flex"
          >
            {SUB_TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  `inline-flex items-center rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-tribal-border text-white"
                      : "text-tribal-muted hover:text-white"
                  }`
                }
              >
                {tab.label}
                <TabCount count={countFor(tab.to)} />
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <nav
        aria-label="Events views (mobile)"
        className="flex flex-shrink-0 overflow-x-auto border-b border-tribal-border bg-tribal-surface sm:hidden"
      >
        {SUB_TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `flex flex-1 items-center justify-center whitespace-nowrap py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "border-b-2 border-tribal-red text-tribal-red"
                  : "text-tribal-muted"
              }`
            }
          >
            {tab.label}
            <TabCount count={countFor(tab.to)} />
          </NavLink>
        ))}
      </nav>

      <div className="min-h-0 flex-1 overflow-hidden">
        <Routes>
          <Route index element={<PipelineView />} />
          <Route path="calls" element={<ScheduledCallsView />} />
          <Route path="calendar" element={<CalendarView />} />
          <Route path="settings" element={<SettingsView />} />
          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </div>
    </div>
  );
}
